import { z } from 'zod'
import { DatesSchema, ParamsSchema, PriceRangeQuerySchema } from '../../common/query-params'
import { CoordinatesSchema } from '../../common'

const ChildAgeSchema = z.object({
  child: z.string(),
  age: z.coerce.number().min(0).max(17),
})

export const RoomSearchParamSchema = z
  .object({
    adults: z.coerce.number().min(1),
    roomNumber: z.coerce.number().min(1),
    children: z.coerce.number().min(0),
    childrenAges: z.array(ChildAgeSchema).optional(),
  })
  .superRefine(({ children, childrenAges }, ctx) => {
    for (let childIndex = 0; childIndex < children; childIndex += 1) {
      if (!childrenAges?.[childIndex]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['childrenAges', childIndex, 'age'],
          message: 'A child age is required',
        })
      }
    }

    if ((childrenAges?.length ?? 0) > children) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_big,
        origin: 'array',
        maximum: children,
        inclusive: true,
        path: ['childrenAges'],
        message: 'The number of child ages must match the number of children',
      })
    }
  })

const HotelFilterArraySchema = z.preprocess((val: unknown) => {
  if (typeof val === 'string') {
    return val.split(',')
  }
  return val
}, z.string().array())

export const HotelShoppingFilterSchema = z.object({
  description: z.string().optional(),
  guestRating: z.string().optional(),
  starRatings: HotelFilterArraySchema.optional(),
  propertyTypes: HotelFilterArraySchema.optional(),
  mealPlans: HotelFilterArraySchema.optional(),
  amenities: HotelFilterArraySchema.optional(),
  accessibilities: HotelFilterArraySchema.optional(),
  priceRange: PriceRangeQuerySchema.optional(),
})

export const HotelDestinationSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  fullName: z.string().min(1),
  name: z.string().min(1).optional(),
})

export const HotelSearchParamsSchema = ParamsSchema.extend({
  rooms: z.array(RoomSearchParamSchema),
  destination: HotelDestinationSchema,
  shoppingFilter: HotelShoppingFilterSchema.optional(),
  balance: z.coerce.number().optional(),
  scrollRef: z.string().optional(),
  geoLocation: z.array(CoordinatesSchema).length(4).nullable().optional(),
})

export const UpgradeHotelDetailsParamsSchema = z.object({
  rooms: z.array(RoomSearchParamSchema),
  bundleId: z.string().min(1),
  recommendationId: z.string().min(1),
  correlationId: z.string().min(1),
  destination: HotelDestinationSchema,
  dates: DatesSchema,
  balance: z.coerce.number().optional(),
})

// either destination.id only (flexi browser)
// or destination.id, dates & rooms (normal search) -- for searching after isFlexi
// or full url details ( both bundleId and recommendationId ) -- for details page
export const HotelDetailsParamsSchema = z
  .object({
    rooms: z.array(RoomSearchParamSchema),
    correlationId: z.string().min(1).optional(),
    // for non flexi flow, we will fetch details based on bundleId and recommendationId
    bundleId: z.string().min(1).optional(),
    recommendationId: z.string().min(1).optional(),
    // for non flexi flow, we will fetch details based on destination.id, dates & rooms
    destination: HotelDestinationSchema.omit({
      type: true,
      fullName: true,
      name: true,
    })
      .extend({ id: z.string().optional() })
      .optional(),
    dates: DatesSchema,
    balance: z.coerce.number().optional(),
    opco: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const hasDestinationId = !!data.destination?.id
    const hasDates = !!data.dates?.startDateTime && !!data.dates?.endDateTime
    const hasTravelers = !!data.rooms?.length
    const hasBundleAndRecommendation = !!data.bundleId && !!data.recommendationId
    const isSearchByDestinationId = hasDestinationId && hasDates && hasTravelers

    // we should have either destination.id, dates & travelers OR both bundleId and recommendationId
    if (!(isSearchByDestinationId || hasBundleAndRecommendation)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Either destination.id & dates & rooms OR both bundleId and recommendationId must be present.',
      })
    }
  })

export const FlexiHotelSearchParamsSchema = z.object({
  destination: HotelDestinationSchema.pick({ id: true }),
})

export const FlexiHotelSearchParamsSchemaStrict = z
  .object({
    destination: HotelDestinationSchema.pick({ id: true }),
    opco: z.string().optional(),
  })
  .strict() // This ensures no extra fields are allowed

// !!! Union schema for hotel details page - the more specific schema first
export const HotelDetailsOrFlexiParamsSchema = z.union([
  HotelDetailsParamsSchema, // More specific - try this first
  FlexiHotelSearchParamsSchemaStrict, // Less specific - fallback
])

export type FlexiHotelSearchParams = z.infer<typeof FlexiHotelSearchParamsSchema>
export type RoomSearchParam = z.infer<typeof RoomSearchParamSchema>
export type HotelSearchParams = z.infer<typeof HotelSearchParamsSchema>
export type HotelDetailsParams = z.infer<typeof HotelDetailsParamsSchema>
export type UpgradeHotelDetailsParams = z.infer<typeof UpgradeHotelDetailsParamsSchema>
export type HotelDetailsOrFlexiParams = z.infer<typeof HotelDetailsOrFlexiParamsSchema>
export type HotelShoppingFilter = z.infer<typeof HotelShoppingFilterSchema>
export type HotelDestination = z.infer<typeof HotelDestinationSchema>

// Type guard functions
export const isFlexiHotelSearchParams = (
  params: HotelDetailsOrFlexiParams
): params is FlexiHotelSearchParams => {
  return Boolean(
    'destination' in params &&
      params.destination &&
      'id' in params.destination &&
      !('rooms' in params) &&
      !('bundleId' in params) &&
      !('recommendationId' in params)
  )
}

export const isHotelDetailsParams = (
  params: HotelDetailsOrFlexiParams
): params is HotelDetailsParams => {
  return !isFlexiHotelSearchParams(params)
}
